import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Mic, MicOff, X, AudioWaveform, Volume2, RotateCcw, Square } from "lucide-react";
import { useAria } from "../context/AriaContext";
import { useAuth } from "../context/AuthContext";
import AriaFeedback from "./AriaFeedback";
import ReasoningPanel, { reasoningForVoiceCard } from "./ReasoningPanel";

// Routes where the FAB should NOT render (public/auth pages)
// Hide on public/landing/login routes and on portal routes. Additionally
// hide on /quick-assist because that route ships a dedicated, pause-
// tolerant microphone controller (see QuickAssist.jsx) that must be the
// only mic visible on the page. Global navigation-microphone behaviour
// on every OTHER route is unchanged.
const HIDE_ON = ["/", "/login", "/signup", "/baa", "/terms", "/accept-baa", "/enroll-2fa", "/quick-assist"];

function isPortalRoute(pathname) {
  return pathname.startsWith("/collab/portal") || pathname.startsWith("/patient-portal");
}

/**
 * Track the bottom offset needed to stay above the mobile keyboard.
 * visualViewport.height shrinks when the soft keyboard opens; we use the
 * delta to push the FAB/card up so neither is hidden.
 */
function useKeyboardOffset() {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const onResize = () => {
      // How much of the layout viewport is hidden by browser UI / keyboard
      const hidden = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setOffset(hidden);
    };
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    onResize();
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
  }, []);
  return offset;
}

export default function AriaFab() {
  const { user } = useAuth();
  const location = useLocation();
  const aria = useAria();
  const [showCard, setShowCard] = useState(false);
  const kbOffset = useKeyboardOffset();

  // Review-mode "Show all / Show less" state. `reviewOverflow` is only set
  // to true when the collapsed transcript actually overflows 3 lines — the
  // toggle is otherwise hidden so short transcripts stay clean.
  const [reviewExpanded, setReviewExpanded] = useState(false);
  const [reviewOverflow, setReviewOverflow] = useState(false);
  const reviewTextRef = useRef(null);

  // Reset expander when a NEW review session appears (identified by `at`),
  // then measure overflow once the collapsed transcript has painted.
  useEffect(() => {
    setReviewExpanded(false);
    setReviewOverflow(false);
    if (!aria.review) return;
    const raf = requestAnimationFrame(() => {
      const el = reviewTextRef.current;
      if (!el) return;
      setReviewOverflow(el.scrollHeight > el.clientHeight + 1);
    });
    return () => cancelAnimationFrame(raf);
  }, [aria.review]);

  // Hide on public/landing/login routes and on portal routes
  const hidden =
    !user ||
    !aria.settings.enabled ||
    HIDE_ON.includes(location.pathname) ||
    isPortalRoute(location.pathname);

  // Auto-show transcript card when speaking, listening, or holding a review.
  useEffect(() => {
    if (aria.speaking || aria.listening || aria.review) setShowCard(true);
  }, [aria.speaking, aria.listening, aria.review]);

  // Route change → cancel any live mic session immediately. This satisfies
  // the founder requirement that the mic stops when the user leaves the
  // page. The AriaContext logout effect handles the sign-out case; the
  // route effect covers everything else (back, nav, deep-link jump).
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      if (aria.listening) {
        try { aria.cancelListen(); } catch (_) { /* ignore */ }
      }
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, aria]);

  // beforeunload → ensure the browser-tab close/reload also cuts the mic.
  useEffect(() => {
    const handler = () => {
      try { aria.cancelListen(); } catch (_) { /* ignore */ }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [aria]);

  /**
   * P0 chairside-speed change (2026-01, updated 2026-02 for founder mic-lag defect):
   *   • No `aria.greet()` on tap — the microphone begins listening
   *     immediately without waiting for any TTS.
   *   • `beginListen()` is called SYNCHRONOUSLY inside the click handler so
   *     iOS Safari / iPadOS Safari (WebKit) still recognise the caller's
   *     gesture as authorising `webkitSpeechRecognition`.
   *   • ONE tap interrupts Rachel AND starts listening in the same gesture
   *     chain. If Rachel is speaking we stop her audio, force-release the
   *     iOS audio session, and immediately proceed to beginListen — the
   *     clinician never needs a second tap to ask a follow-up (2026-02
   *     founder mic-lag repair).
   *   • `primeAudio()` is idempotent per-tab: if the audio element was
   *     already unlocked earlier this session we SKIP the re-prime. Otherwise
   *     the silent-MP3 replay would re-acquire the iOS audio session that
   *     stopAudio() just released, starving the SR mic of audio and clipping
   *     the clinician's first word.
   *   • `onClick` is preferred over `onPointerDown` because:
   *       - `pointerdown` misfires on scroll/swipe over a floating button,
   *       - `click` still satisfies the WebKit gesture requirement, and
   *       - the 50-150ms saved by `pointerdown` is dwarfed by the 2-4 s
   *         we already save by removing the greeting.
   *   • Debounce: the recognizer itself enforces `!listeningRef.current`.
   */
  const onClick = () => {
    if (aria.listening) {
      // Second tap while recording = Stop & Submit (not Cancel — that would
      // silently discard a case the clinician just spoke). Cancel is a
      // separate button in the recording card.
      aria.stopListen();
      return;
    }
    // If Rachel is still speaking, stop her IN THE SAME GESTURE and fall
    // through to beginListen. iOS treats this whole handler as one gesture
    // so WebKit authorises both the audio-stop and the SR.start(). The old
    // behavior — stop() then early-return — forced the clinician to tap
    // twice, which is the founder-reported "follow-up mic lag" defect.
    if (aria.speaking) {
      aria.stopAudio();
    }
    setShowCard(true);
    // Prime iOS Safari audio ONCE per tab session. Re-priming on every mic
    // tap would replay a silent MP3 that re-acquires the iOS audio session
    // and starves SR of the microphone for ~200-500ms — clipping the first
    // word of every follow-up. primeAudio() itself is now guarded by
    // `_audioUnlocked` so this is a no-op after the first successful prime.
    try { aria.primeAudio && aria.primeAudio(); } catch (_) { /* best-effort */ }
    // Fire synchronously — do not await. Any await here would break the
    // WebKit user-gesture chain.
    aria.beginListen();
  };

  if (hidden) return null;

  return (
    <>
      {/* Transcript / status card */}
      {showCard && (aria.lastSpoken || aria.transcript || aria.listening || aria.speaking) && (
        <div
          data-testid="aria-card"
          className="fixed left-3 right-3 sm:left-auto sm:right-6 z-50 max-w-none sm:max-w-sm bg-[var(--pa-bg-2)] border border-[var(--pa-gold)]/40 rounded-lg shadow-2xl shadow-black/50 backdrop-blur-sm p-4 max-h-[70vh] overflow-y-auto"
          style={{
            bottom: `calc(${kbOffset + 88}px + env(safe-area-inset-bottom, 0px))`,
            animation: "ariaCardIn 200ms ease-out",
          }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <AudioWaveform size={14} className="text-[var(--pa-gold)] shrink-0" />
              <span className="text-[10px] uppercase tracking-widest text-[var(--pa-gold)]">ARIA</span>
              {aria.listening && (
                <span data-testid="aria-listening" className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-red-400">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                  Listening
                </span>
              )}
              {aria.speaking && !aria.listening && (
                <span data-testid="aria-speaking" className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--pa-gold)]">
                  <Volume2 size={11} className="animate-pulse" /> Speaking
                </span>
              )}
            </div>
            <button
              onClick={() => setShowCard(false)}
              data-testid="aria-card-close"
              className="text-[var(--pa-text-muted)] hover:text-[var(--pa-text)] shrink-0"
              aria-label="Close ARIA card"
            >
              <X size={14} />
            </button>
          </div>
          {aria.lastSpoken && (
            <p className="mt-2 text-sm text-[var(--pa-text)] leading-snug" data-testid="aria-spoken">
              {aria.lastSpoken}
            </p>
          )}
          {aria.ttsUnavailable && (
            <div
              data-testid="aria-tts-unavailable"
              role="status"
              aria-live="polite"
              className="mt-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/40 text-[11px] text-amber-200 leading-snug"
            >
              <span className="font-semibold">ARIA audio is temporarily unavailable.</span>{" "}
              The written response above is complete. ARIA will read it aloud once the voice service returns.
            </div>
          )}
          {aria.lastSpoken && (aria.canReplay || aria.isPlaying) && aria.settings.spokenResponses !== false && (
            <div className="mt-3 flex items-center gap-2" data-testid="aria-audio-controls">
              {aria.isPlaying ? (
                <button
                  type="button"
                  onClick={() => aria.stopAudio()}
                  data-testid="aria-stop-audio"
                  aria-label="Stop ARIA audio"
                  className="inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-md border border-[var(--pa-border)] text-[11px] text-[var(--pa-text-muted)] hover:text-[var(--pa-text)] hover:border-[var(--pa-text-muted)] transition"
                >
                  <Square size={12} /> Stop audio
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => aria.replayResponse()}
                  data-testid="aria-replay"
                  aria-label="Replay ARIA response"
                  disabled={!aria.canReplay}
                  className="inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-md border border-[var(--pa-gold)]/60 text-[11px] text-[var(--pa-gold)] hover:bg-[var(--pa-gold)]/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <RotateCcw size={12} /> Replay ARIA response
                </button>
              )}
            </div>
          )}
          {aria.review && !aria.listening && (
            <div className="mt-3 pt-3 border-t border-[var(--pa-border)]" data-testid="aria-review">
              <div className="kicker mb-1 flex items-center justify-between">
                <span>Review your question</span>
                <span className="text-[10px] text-[var(--pa-text-dim)]">
                  {aria.review.reason === "max_reached" ? "Time limit" : aria.review.reason === "stop" ? "Stopped" : "Ready"}
                </span>
              </div>
              {aria.review.reason === "max_reached" && (
                <div
                  data-testid="aria-review-max-notice"
                  className="mb-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/40 text-[11px] text-amber-200"
                  role="status"
                  aria-live="polite"
                >
                  Maximum recording time reached. Review your question, then tap Submit.
                </div>
              )}
              <div
                data-testid="aria-review-transcript"
                ref={reviewTextRef}
                className={`p-2 rounded-md bg-[var(--pa-bg-2)] border border-[var(--pa-border)] text-sm text-[var(--pa-text)] leading-snug ${reviewExpanded ? "max-h-[40vh] overflow-y-auto" : ""}`}
                style={reviewExpanded ? undefined : {
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {aria.review.text}
              </div>
              {reviewOverflow && (
                <button
                  type="button"
                  onClick={() => setReviewExpanded((v) => !v)}
                  data-testid="aria-review-toggle"
                  aria-expanded={reviewExpanded}
                  className="mt-1 text-[10px] uppercase tracking-widest text-[var(--pa-gold)] hover:underline focus:outline-none focus:ring-1 focus:ring-[var(--pa-gold)] rounded"
                >
                  {reviewExpanded ? "Show less" : "Show all"}
                </button>
              )}
              {aria.review.corrections && aria.review.corrections.length > 0 && (
                <div
                  data-testid="aria-review-corrections"
                  className="mt-2 text-[10px] text-[var(--pa-text-muted)]"
                >
                  <span className="uppercase tracking-widest text-[var(--pa-gold)] mr-1">Corrected:</span>
                  {aria.review.corrections.slice(0, 4).map((c, i) => (
                    <span key={`${c.variantId || c.variant}-${i}`} className="inline-block mr-2">
                      <span className="line-through text-[var(--pa-text-dim)]">{c.variant}</span>
                      <span className="text-[var(--pa-gold)]"> → {c.canonical}</span>
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  onClick={() => aria.cancelReview()}
                  data-testid="aria-review-cancel"
                  className="text-xs font-medium min-h-[44px] min-w-[44px] rounded-md border border-[var(--pa-border)] text-[var(--pa-text-muted)] hover:text-[var(--pa-text)] hover:border-[var(--pa-text-muted)] transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => aria.submitReview()}
                  data-testid="aria-review-submit"
                  className="text-xs font-semibold min-h-[44px] min-w-[44px] rounded-md bg-[var(--pa-gold)] text-[var(--pa-bg)] hover:bg-[var(--pa-gold-hover)] transition"
                >
                  Submit
                </button>
              </div>
            </div>
          )}
          {aria.listening && aria.recording && (
            <div className="mt-3 pt-3 border-t border-[var(--pa-border)]" data-testid="aria-recording-controls">
              {(() => {
                const elapsed = Math.max(0, aria.recording.elapsed_ms || 0);
                const max = aria.recording.max_ms || 45000;
                const pct = Math.min(100, Math.round((elapsed / max) * 100));
                const ss = Math.floor(elapsed / 1000);
                const rem = Math.max(0, Math.ceil((max - elapsed) / 1000));
                const warning = !!aria.recording.warned;
                return (
                  <>
                    <div className="flex items-center justify-between text-[10px] font-mono">
                      <span data-testid="aria-recording-timer" className={warning ? "text-red-400" : "text-[var(--pa-gold)]"}>
                        {String(Math.floor(ss / 60)).padStart(2, "0")}:{String(ss % 60).padStart(2, "0")} / 00:45
                      </span>
                      <span className={warning ? "text-red-400 uppercase tracking-wider" : "text-[var(--pa-text-dim)] uppercase tracking-wider"}>
                        {warning ? `${rem}s left` : "Recording"}
                      </span>
                    </div>
                    <div className="mt-1.5 h-1 rounded bg-[var(--pa-border)] overflow-hidden">
                      <div
                        className={`h-full transition-all ${warning ? "bg-red-500" : "bg-[var(--pa-gold)]"}`}
                        style={{ width: `${pct}%` }}
                        data-testid="aria-recording-progress"
                      />
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button
                        onClick={() => aria.cancelListen()}
                        data-testid="aria-recording-cancel"
                        className="text-xs font-medium min-h-[44px] min-w-[44px] rounded-md border border-[var(--pa-border)] text-[var(--pa-text-muted)] hover:text-[var(--pa-text)] hover:border-[var(--pa-text-muted)] transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => aria.stopListen()}
                        data-testid="aria-recording-stop"
                        className="text-xs font-semibold min-h-[44px] min-w-[44px] rounded-md border border-[var(--pa-gold)] text-[var(--pa-gold)] hover:bg-[var(--pa-gold)]/10 transition"
                      >
                        Stop
                      </button>
                    </div>
                    {warning && (
                      <div
                        data-testid="aria-recording-warning"
                        className="mt-2 text-[10px] text-red-300 uppercase tracking-wider"
                        role="status"
                        aria-live="polite"
                      >
                        Approaching 45s limit — the transcript will be preserved.
                      </div>
                    )}
                    {aria.recording.silence_warning && !warning && (
                      <div
                        data-testid="aria-recording-silence-warning"
                        className="mt-2 text-[11px] italic text-[var(--pa-text-muted)]"
                        role="status"
                        aria-live="polite"
                      >
                        Still recording — continue speaking or tap Stop.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
          {aria.transcript && (
            <div className="mt-3 pt-3 border-t border-[var(--pa-border)]">
              <div className="kicker mb-1">You said</div>
              <p className="text-xs text-[var(--pa-text-muted)] italic" data-testid="aria-transcript">
                "{aria.transcript}"
              </p>
            </div>
          )}
          {!aria.recognitionAvailable && (
            <div className="mt-3 text-[10px] text-[var(--pa-text-dim)]">
              Voice input not supported in this browser. Use Chrome, Edge, or Safari.
            </div>
          )}
          {aria.lastSpoken && !aria.listening && !aria.speaking && (
            <>
              <ReasoningPanel
                surface="voice_card"
                compact
                reasoning={reasoningForVoiceCard(aria.lastSpoken, aria.transcript || null)}
              />
              <AriaFeedback
                surface="voice_card"
                ariaResponse={aria.lastSpoken}
                originalQuery={aria.transcript || null}
                compact
              />
            </>
          )}
        </div>
      )}

      {/* Floating gold button */}
      <button
        onClick={onClick}
        data-testid="aria-fab"
        aria-label="ARIA clinical assistant"
        className="fixed right-4 sm:right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl shadow-black/50 transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--pa-gold)] focus:ring-offset-2 focus:ring-offset-[var(--pa-bg)]"
        style={{
          bottom: `calc(${kbOffset + 16}px + env(safe-area-inset-bottom, 0px))`,
          background:
            aria.listening
              ? "linear-gradient(135deg, #ef4444, #b91c1c)"
              : "linear-gradient(135deg, #E6C36A, #D4AF37 60%, #A8842A)",
          boxShadow: aria.speaking
            ? "0 0 0 4px rgba(212,175,55,0.25), 0 0 24px 6px rgba(212,175,55,0.5)"
            : aria.listening
            ? "0 0 0 4px rgba(239,68,68,0.30), 0 0 22px 6px rgba(239,68,68,0.5)"
            : "0 8px 20px rgba(0,0,0,0.45)",
          animation: aria.speaking ? "ariaPulse 1.6s ease-in-out infinite" : aria.listening ? "ariaPulseRed 1s ease-in-out infinite" : "none",
        }}
      >
        {aria.listening ? (
          <MicOff size={20} color="#fff" strokeWidth={2} />
        ) : (
          <Mic size={20} color="#fff" strokeWidth={2} />
        )}
        <span className="sr-only">ARIA</span>
        <span
          className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest text-[var(--pa-gold)] pointer-events-none"
          data-testid="aria-fab-label"
        >
          ARIA
        </span>
      </button>

      {/* Inline keyframes — kept colocated so we don't depend on Tailwind config edits */}
      <style>{`
        @keyframes ariaPulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(212,175,55,0.25), 0 0 24px 6px rgba(212,175,55,0.45); transform: scale(1); }
          50% { box-shadow: 0 0 0 10px rgba(212,175,55,0.0), 0 0 36px 12px rgba(212,175,55,0.65); transform: scale(1.04); }
        }
        @keyframes ariaPulseRed {
          0%, 100% { box-shadow: 0 0 0 4px rgba(239,68,68,0.30), 0 0 22px 6px rgba(239,68,68,0.5); }
          50% { box-shadow: 0 0 0 10px rgba(239,68,68,0.0), 0 0 32px 12px rgba(239,68,68,0.7); }
        }
        @keyframes ariaCardIn {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
